import type { PaymentsMessageId } from "./payments.fr";

/** Paiements in English. Same keys and {placeholders} as payments.fr.ts. */
export const paymentsEn: Record<PaymentsMessageId, string> = {
  "payments.tab.payments": "Payments",
  "payments.tab.approvals": "Approval requests",
  "payments.pick": "Pick a payment from the list.",
  "payments.loading": "Loading the payment…",
  "payments.loadFailed": "Couldn't load this payment.",
  "payments.notFound": "This payment can't be found. Reload the list.",
  "payments.refreshFailed": "The payment couldn't be refreshed: it's shown as it was.",

  "payments.filter.provider": "Payment method",
  "payments.filter.status": "Status",
  "payments.filter.all": "All",
  "payments.filter.from": "From",
  "payments.filter.to": "To",
  "payments.filter.duplicates": "Likely duplicates",
  "payments.filter.clearPro": "All pros",
  "payments.list.loading": "Loading payments…",
  "payments.list.loadFailed": "Couldn't load the payments.",
  "payments.list.empty": "No payments match these filters.",
  "payments.list.aria": "Fee payments",
  "payments.list.end": "End of the list",
  "payments.pill.duplicate": "Likely duplicate",

  "payments.status.pending": "Due",
  "payments.status.paid": "Paid",
  "payments.status.failed": "Failed",
  "payments.status.refunded": "Refunded",
  "payments.status.waived": "Fee waived",
  "payments.provider.orange_money": "Orange Money",
  "payments.provider.lengo_pay": "Mobile Money",
  "payments.provider.stripe": "Card (Stripe)",
  "payments.provider.wave": "Wave",
  "payments.feeType.per_job": "Introduction fee",
  "payments.feeType.monthly": "Monthly subscription",
  "payments.feeType.yearly": "Yearly subscription",
  "payments.kind.refund": "Refund",
  "payments.kind.credit": "Credit",
  "payments.kind.waive": "Fee waiver",
  "payments.kind.mark_paid": "Marked paid",

  "payments.detail.versionTitle": "The version of the payment on screen: every correction sends it.",
  "payments.detail.duplicate": "Likely duplicate",
  "payments.detail.duplicateBody":
    "Another payment from the same pro, for the same amount, a few minutes earlier or later. Check before refunding:",
  "payments.detail.pro": "Professional",
  "payments.detail.proPayments": "Their payments →",
  "payments.detail.transaction": "Provider reference",
  "payments.detail.job": "Request",
  "payments.detail.refunded": "Already refunded",
  "payments.detail.refundable": "Still refundable",
  "payments.detail.credit": "Pro's credit",
  "payments.detail.adjustments": "Adjustments",
  "payments.detail.noAdjustments": "No adjustments on this payment.",
  "payments.detail.actions": "Correct",
  "payments.detail.neverOptimistic":
    "Nothing is shown as done before the server answers. A refund with no answer from the provider is never sent again.",
  "payments.col.kind": "Adjustment",
  "payments.col.amount": "Amount",
  "payments.col.status": "State",
  "payments.col.by": "By",
  "payments.col.actions": "Action",

  "payments.adjustment.status.pending": "Status unknown, checking",
  "payments.adjustment.status.done": "Done",
  "payments.adjustment.status.failed": "Refused by the provider",
  "payments.adjustment.status.awaiting_manual_transfer": "Manual transfer to make",
  "payments.adjustment.status.awaiting_approval": "Awaiting approval",
  "payments.adjustment.status.rejected": "Refused by the owner",
  "payments.adjustment.outcomeUnknownHint":
    "Sent to the provider with no answer. It's never sent again: check the provider's dashboard, then settle it.",
  "payments.adjustment.manualHint":
    "Orange Money has no automatic refund: send the money by hand, then settle it with the transfer reference.",

  "payments.action.refund": "Refund",
  "payments.action.credit": "Credit",
  "payments.action.waive": "Waive the fee",
  "payments.action.markPaid": "Mark paid",
  "payments.action.settle": "Settle",
  "payments.action.approve": "Approve",
  "payments.action.reject": "Reject",

  "payments.dialog.refund.title": "Refund · {subject}",
  "payments.dialog.refund.body":
    "The refund is saved, then sent to the provider once only. Above the limit, it waits for an owner's approval.",
  "payments.dialog.credit.title": "Credit · {subject}",
  "payments.dialog.credit.body": "A balance taken off the pro's next fees, in the same currency.",
  "payments.dialog.waive.title": "Waive the fee · {subject}",
  "payments.dialog.waive.body": "The fee is no longer owed. Above the limit, the waiver waits for an owner's approval.",
  "payments.dialog.markPaid.title": "Mark paid · {subject}",
  "payments.dialog.markPaid.body":
    "Only with the reference of the real payment (the pro's SMS or the statement). A reference marks one payment only.",
  "payments.dialog.settle.title": "Settle the refund · {subject}",
  "payments.dialog.settle.body": "Record how the refund ended, once checked with the provider.",
  "payments.dialog.approve.title": "Approve {subject}'s request",
  "payments.dialog.approve.body": "The requested correction applies right away. No one approves their own request.",
  "payments.dialog.reject.title": "Reject {subject}'s request",
  "payments.dialog.reject.body": "Nothing is refunded or waived. The reason is kept with the request.",
  "payments.field.amount": "Amount ({currency})",
  "payments.field.refundHint": "Leave empty to refund everything: {amount}.",
  "payments.field.orangeMoneyReference": "Orange Money transaction reference",
  "payments.field.reference": "Provider reference",
  "payments.field.referenceHint": "Copy it in full from the SMS or statement: letters, digits and . _ : / -",
  "payments.field.outcome": "Outcome",
  "payments.field.outcome.done": "The money went out",
  "payments.field.outcome.failed": "The refund failed",

  "payments.problem.reason": "Give a reason of at least {min} characters.",
  "payments.problem.amount": "Enter an amount above zero, with at most two decimals.",
  "payments.problem.tooMuch": "That's more than what's left to refund.",
  "payments.problem.reference":
    "Copy the reference in full: at least {min} characters, letters, digits and . _ : / - only.",

  "payments.outcome.done": "{kind} of {amount}: done.",
  "payments.outcome.pending":
    "{kind} of {amount}: status unknown, checking. It won't be sent again; settle it once checked.",
  "payments.outcome.awaiting_manual_transfer":
    "{kind} of {amount} saved: send the money by hand, then settle it with the reference.",
  "payments.outcome.awaiting_approval": "{kind} of {amount}: beyond what you may do alone, it waits for approval.",
  "payments.outcome.failed": "{kind} of {amount} refused by the provider ({failure}). Nothing moved.",
  "payments.outcome.rejected": "{kind} of {amount} rejected.",

  "payments.approvals.status.pending": "Pending",
  "payments.approvals.status.approved": "Approved",
  "payments.approvals.status.rejected": "Rejected",
  "payments.approvals.loading": "Loading requests…",
  "payments.approvals.loadFailed": "Couldn't load the approval requests.",
  "payments.approvals.empty": "No requests here.",
  "payments.approvals.requested": "Requested by {name} on {date}",
  "payments.approvals.permission": "Permission: {permission}",
  "payments.approvals.theirLimit": "Their limit: {amount}",
  "payments.approvals.noPermission": "They don't hold this permission.",
  "payments.approvals.noLimitInCurrency": "They have no limit in this currency.",
  "payments.approvals.openPro": "Open the pro →",
  "payments.approvals.decided": "Decided by {name} on {date}",
  "payments.approvals.openPayment": "Open the payment →",
  "payments.approvals.own": "This is your request: another owner must decide it.",

  "payments.export.month": "Month to export",
  "payments.export.reason": "Reason (optional)",
  "payments.export.button": "Export CSV",
  "payments.export.running": "Exporting…",
  "payments.export.hint":
    "The month's payments (UTC) for reconciliation, with no phone numbers or emails. Every export is recorded in the audit log.",
  "payments.export.done": "Export downloaded. It's recorded in the audit log.",

  "payments.error.stale": "This payment changed while you were looking at it. Reload it, check, then try again.",
  "payments.error.refused":
    "Correction refused: your roles don't include the feature needed. Ask a manager for access.",
  "payments.error.approvalRefused":
    "Refused: only an owner with APPROVE_PAYMENT_REQUESTS can decide, and never on their own request.",
  "payments.error.invalid":
    "Refused: the payment is no longer in the expected state, the amount exceeds what's left, or the reference is already used. Reload and check.",
  "payments.error.outcomeUnknown":
    "No answer from the server: the correction may have been saved. Try again: the same click can't be applied twice.",

  "payments.refused.notRefundable": "Refused: only a paid fee can be refunded. Reload the payment.",
  "payments.refused.nothingToRefund":
    "Refused: this fee is already refunded in full, or the rest is waiting. Reload the payment.",
  "payments.refused.refundTooLarge": "Refused: the amount is more than what's left to refund on this fee.",
  "payments.refused.alreadyAwaitingApproval":
    "Refused: a waiver of this fee is already waiting for an owner. Reload the payment.",
  "payments.refused.notMarkable": "Refused: only a pending or failed fee can be marked paid. Reload the payment.",
  "payments.refused.referenceInUse":
    "Refused: this Orange Money reference already paid another fee. Check the reference.",
  "payments.refused.referenceRequired": "Enter the transaction reference.",
  "payments.refused.alreadySettled": "This refund was settled a moment ago. Reload it.",
  "payments.refused.alreadyDecided": "This request was decided a moment ago. Reload the list.",
  "payments.refused.ownRequest": "Refused: you made this request, so another owner decides.",
  "payments.refused.approverNotPermitted":
    "You don't hold this permission for this amount, so you can't decide it. Someone else decides.",
  "payments.refused.paymentChanged":
    "Refused: the payment changed since the request. Reject it instead, with the reason.",
  "payments.refused.currencyMismatch": "Refused: the credit must be in the fee's currency.",
  "payments.refused.feeOfAnotherPro": "Refused: this fee was paid by another pro.",
  "payments.refused.invalidAmount": "Refused: the amount must be more than zero.",
};
